import React from 'react';
import { ColorFrom, Feature } from '@tencent/workbench';
import { AngleFrom, NumberInputFrom, ColorOperations } from '@tencent/wb-ext-form';
import BaseDrawerEffectFeature from '@framework/workbench/common/feature/base-drawer-effect-feature';
import { EffectPropsField } from '@editor/src/js/constants';
import ShadowPresetMenu, {
    ShadowStyleAndPropsMapping,
} from '@src/framework/frame-pc/components/palette-panel/effect-preset-menu/shadow-preset-menu/index-text';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import PresetPanel from '@framework/workbench/drawer/components/preset-panel';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { IShadowModelVal } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import _isEqualWith from 'lodash/isEqualWith';
import { MULTI } from '@src/js/constant';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import { getDrawerColorOptions } from '@framework/workbench/drawer/utils/color-options';
import {
    TDWActionType, TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

interface IDefaultShadowProps {
    color?: string;
    tdColor?: ITDColor;
    opacity?: number;
    size?: number;
    blurRad?: number;
    angel?: number;
    dist?: number;
    isOuter?: boolean;
}

const defaultShadowProps: IDefaultShadowProps = {
    color: '#000000',
    tdColor: new TDColor({ rgbaStr: '#000000' }),
    opacity: 0,
    size: 0,
    blurRad: 0,
    angel: 0,
    dist: 0,
    isOuter: true, /** 默认是外阴影 */
};
const SHADOW_EMPTY = 'shadow-empty';
const SHADOW_TYPE = {
    OUTER: 'outer',
    INNER: 'inner',
    PERSPECTIVE: 'perspective',
};
const DEFAULT_EMPTY_COLOR = '#000000';

@Feature()
export class ShadowFeature extends BaseDrawerEffectFeature {
    public id = ActionType.FONT_EFFECT;

    // 侧边栏展示数据
    public shadowInfoInDrawer: IDefaultShadowProps = { ...defaultShadowProps };

    // 预览时的临时数据
    private shadowInfoInPreview;

    // 获取uiModel上的数据
    public get shadowInfoInModel(): IShadowModelVal {
        const data = this.getFontEffect(ActionType.SHADOW);
        return (data === MULTI ? null : data) ?? defaultShadowProps;
    }

    // 是否是部分设置了效果或者不同部分设置了不同的效果
    public get isMultiDifferent(): boolean {
        return this.isTextMultiDiffValue(ActionType.SHADOW);
    }

    // 获取侧边栏大小数据（预览态有值时说明处于预览态，优先预览态数据）
    public get size(): number {
        return (this.shadowInfoInPreview || this.shadowInfoInDrawer).size;
    }

    // 获取侧边栏模糊数据（预览态有值时说明处于预览态，优先预览态数据）
    public get blur(): number {
        return (this.shadowInfoInPreview || this.shadowInfoInDrawer).blurRad;
    }

    // 获取侧边栏距离数据（预览态有值时说明处于预览态，优先预览态数据）
    public get dist(): number {
        return (this.shadowInfoInPreview || this.shadowInfoInDrawer).dist;
    }

    // 获取侧边栏透明度数据（预览态有值时说明处于预览态，优先预览态数据）
    public get opacity(): number {
        return Math.round(Number((this.shadowInfoInPreview || this.shadowInfoInDrawer).opacity)) || 0;
    }

    // 获取侧边栏角度数据（预览态有值时说明处于预览态，优先预览态数据）
    public get dir(): number {
        const info = this.shadowInfoInPreview || this.shadowInfoInDrawer;
        let value = info.dir || info.angel || 0;
        if (typeof value !== 'number') {
            value =  Number(value?.match(/\d*/)[0]);
        }
        return value;
    }

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_SHADOW);
    }

    public initPropsCallback = (): void => {
        // 点击文字时，侧边栏同步大小透明度等数据
        if (!this.getTextEditorStateService().isTextPreview) {
            this.shadowInfoInPreview = undefined;
            this.shadowInfoInDrawer = { ...this.shadowInfoInModel };
        }
    };

    public shouldSizeDisabled = () => (this.isMultiModel() && this.isTable()) || !this.shadowInfoInModel.isOuter;

    // 阴影类型切换面板
    public renderPreset(): any {
        // 表格切换单元格的数值更新，可以靠slide-adaptor触发的onSelectionChange联动触发
        this.initPropsCallback();
        const { iconSrc, iconName } = this.getPresetStyle(this.shadowInfoInModel);
        // 当前预览icon
        return <PresetPanel
            src={iconSrc}
            hasEffect={iconName !== SHADOW_EMPTY}
            isMultiDifferent={this.isMultiDifferent}
            onVisibleChange={this.handlePresetVisibleChange}
            placement={'top'}
            disabled={this.isMultiModel() && this.isTable()}
        >
            {/* 详细菜单 */}
            {
                this.getPresetMenuContent()
            }
        </PresetPanel>;
    }

    public getPresetMenuContent = (props?: { style: React.CSSProperties }) => {
        const { iconName } = this.getPresetStyle(this.shadowInfoInModel);
        return (<ShadowPresetMenu
            containerStyle={props?.style}
            preset={{ style: iconName }}
            onShadowPresetStyleClick={shadow => this.handleShadowPresetStyleClick(shadow)}
            onShadowPresetStyleMouseEnter={shadow => this.handleShadowPresetStyleEnter(shadow)}
            onShadowPresetStyleMouseLeave={() => this.handleShadowPresetStyleMouseLeave()} />);
    };

    /**
     * 阴影各项指标编辑
     * @param value 指标值
     * @param isPreview 是否是预览（比如拖拽过程中）
     * @param from 编辑来源
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_SHADOW)
    public handleShadowChange(
        value: Partial<IShadowModelVal>,
        dragEnd?: boolean,
        from?: NumberInputFrom | AngleFrom,
    ): void {
        const isPreview = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isPreview) {
            // 拖拽滑动过程中预览
            this.shadowInfoInPreview = { ...this.shadowInfoInModel, ...value };
            // 因为文字效果实时更新太卡，所以拖拽过程中不渲染效果
            return;
        }
        // 点击、输入、滑动停止
        this.shadowInfoInDrawer = { ...this.shadowInfoInModel, ...value };

        // 更新当前预览状态
        this.updatePreviewState(isPreview);
        // 更新文字ui，非预览态则还要上传sdk
        this.updateUiAndSdk();
        this.handleShadowReport(value, from, isPreview);
    }

    /**
     * 颜色编辑
     * @param value
     * @param from
     */
    public handleColorChange = (
        value: Partial<IShadowModelVal>,
        from: ColorFrom | number,
    ): void => {
        this.updatePreviewState(false);
        this.shadowInfoInDrawer = { ...this.shadowInfoInModel, ...value };
        this.updateUiAndSdk();
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: value.color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_SHADOW,
        });
    };

    /**
     * 颜色选则面板对应的各种操作响应
     * @param value
     * @param from
     * @param mouseMoveType
     */
    public handleColorPanelOperate = (
        value: ITDColor,
        from: ColorFrom,
    ): void => {
        const options = { color: value.srgb_color, tdColor: value, opacity: alphaToOpacity(value.alpha) };
        this.handleColorChange(options, from);
    };

    // 阴影类型切换面板打开时同步文本数据
    public handlePresetVisibleChange = (visible: boolean): void => {
        if (visible) {
            this.shadowInfoInDrawer = { ...this.shadowInfoInModel };
        }
    };

    public getColorSelectButton() {
        return <ColorSelectButton
            onColorHover={this.handleColorEnter}
            onMouseLeave={this.handlePaletteLeave}
            onColorChange={this.handleColorPanelOperate}
            value={this.shadowInfoInModel?.tdColor || new TDColor({
                rgbaStr: DEFAULT_EMPTY_COLOR,
            })}
            paletteButtonsStyle={{
                marginLeft: 'auto',
                boxSizing: 'border-box',
            }}
        />;
    }

    // 更新文字ui， 当处于非预览态时会同时上传sdk（是否处于预览态靠editor更新的获取store状态判断）
    private updateUiAndSdk = (): void => {
        const data = this.getTextEditorStateService().isTextPreview
            ? { ...this.shadowInfoInPreview } : { ...this.shadowInfoInDrawer };
        if (data.opacity) {
            data.tdColor?.updateOpacity(data.opacity);
        }
        const values = { [ActionType.EFFECT]: {} };
        if (data && !_isEqualWith(data, defaultShadowProps)) {
            // sizeX | sizeY 不可为空，为空报错
            data.sizeX = data.sizeX === undefined ? data.size : data.sizeX;
            data.sizeY = data.sizeY === undefined ? data.size : data.sizeY;
            if (!data.skewX) {
                delete data.skewX;
            }
            values[ActionType.EFFECT][ActionType.SHADOW] = data;
        } else {
            values[ActionType.EFFECT][ActionType.SHADOW] = null;
        }
        this.updateProps(this.id, values);
    };

    // hover 到颜色上时预览属性
    private handleColorEnter = (color: string): void => {
        this.updatePreviewState(true);
        this.shadowInfoInPreview = { ...this.shadowInfoInDrawer, color };
        this.updateUiAndSdk();
    };

    // 离开颜色面板
    private handlePaletteLeave = (): void => {
        this.shadowInfoInPreview = { ...this.shadowInfoInDrawer };
        this.updateUiAndSdk();
        this.updatePreviewState(false);
    };

    // 从‘预设’面板切换不同的阴影类型
    private handleShadowPresetStyleClick = (shadow: IShadowModelVal): void => {
        this.updatePreviewState(false);
        this.shadowInfoInDrawer = { ...defaultShadowProps, ...{ algn: null }, ...shadow };
        this.updateUiAndSdk();
        this.handleShadowPresetReport(shadow);
    };

    // hover进入不同的阴影类型
    private handleShadowPresetStyleEnter = (shadow: IShadowModelVal) => {
        this.updatePreviewState(true);
        this.shadowInfoInPreview = { ...defaultShadowProps, ...shadow };
        this.updateUiAndSdk();
    };

    // hover离开
    private handleShadowPresetStyleMouseLeave = () => {
        this.shadowInfoInPreview = { ...this.shadowInfoInDrawer };
        if (this.getTextEditorStateService().isTextPreview) {
            // hover离开时判断是否处于预览状态，因为点击选择后也会触发leave
            this.callbackByActionType(ActionType.FONT_REFRESH);
            this.updatePreviewState(false);
        }
    };

    // 更新全局文本预览状态（如果是退出预览态则同时清空预览数据）
    private updatePreviewState = (isPreview: boolean) => {
        this.getTextEditorStateService().isTextPreview = isPreview;
        if (!isPreview) {
            this.shadowInfoInPreview = undefined;
        }
    };

    // 获取阴影类型图标信息
    private getPresetStyle = (shadowInfo: IShadowModelVal) => {
        const iconName = this.getIconName(shadowInfo);
        const iconSrc = ShadowPresetMenu.PresetIconOnShow[iconName];
        return { iconSrc, iconName };
    };

    // 获取透视图标
    // eslint-disable-next-line complexity
    private getPerspectiveIconName(sizeY: number, skewX?: number): string | undefined {
        let iconName;
        if (skewX === undefined || isNaN(skewX) || skewX === 0) {
            iconName = `${SHADOW_TYPE.PERSPECTIVE}-3`;
        } else if (skewX > 0 && sizeY > 0) {
            iconName = `${SHADOW_TYPE.PERSPECTIVE}-1`;
        } else if (skewX > 0 && sizeY < 0) {
            iconName = `${SHADOW_TYPE.PERSPECTIVE}-4`;
        } else if (skewX < 0 && sizeY > 0) {
            iconName = `${SHADOW_TYPE.PERSPECTIVE}-2`;
        } else if (skewX < 0 && sizeY < 0) {
            iconName = `${SHADOW_TYPE.PERSPECTIVE}-5`;
        }
        return iconName;
    }

    // 根据algn获取对应的外阴影图标
    private getOuterIconNameByAlgn(algn): string | undefined {
        const algnToOuter = {};
        Object.keys(ShadowStyleAndPropsMapping).forEach((key) => {
            if (key.includes(`${SHADOW_TYPE.OUTER}-`)) {
                algnToOuter[ShadowStyleAndPropsMapping[key].algn] = key;
            }
        });
        return algnToOuter[algn];
    }

    // 根据角度获取对应的外阴影或者内阴影图标
    private getIconByDir(mainType: string, dir: number, dist: number): string | undefined {
        let iconName;
        if (dir === 0 && dist === 0) {
            iconName = `${mainType}-0-phai`;
        } else if (dir === 0) {
            iconName = `${mainType}-0`;
        } else if (dir < 90) {
            iconName = `${mainType}-45`;
        } else if (dir === 90) {
            iconName = `${mainType}-90`;
        } else if (dir < 180) {
            iconName = `${mainType}-135`;
        } else if (dir === 180) {
            iconName = `${mainType}-180`;
        } else if (dir < 270) {
            iconName = `${mainType}-225`;
        } else if (dir === 270) {
            iconName = `${mainType}-270`;
        } else {
            iconName = `${mainType}-315`;
        }
        return iconName;
    }

    // 获取阴影详细分类
    private getIconName(shadowInfo: IShadowModelVal) {
        const mainType = this.getShadowMainType(shadowInfo);
        const { dir = 0, dist = 0, size, blurRad = 0, sizeY = 0, skewX, algn } = shadowInfo;
        // 无效果
        if (dir === 0 && dist === 0 && size <= 100 && blurRad === 0) {
            return SHADOW_EMPTY;
        }
        // 有效果
        let iconName;
        if (mainType === SHADOW_TYPE.PERSPECTIVE) {
            // 透视
            iconName = this.getPerspectiveIconName(sizeY, skewX);
        } else {
            // 外阴影
            if (shadowInfo.isOuter) {
                iconName = this.getOuterIconNameByAlgn(algn);
            }
            // 内阴影
            if (!shadowInfo.isOuter) {
                iconName = this.getIconByDir(mainType, dir, dist);
            }
        }
        return iconName || SHADOW_EMPTY;
    }

    // 获取阴影主分类
    private getShadowMainType(shadowInfo: IShadowModelVal): string {
        const { isOuter, sizeX, sizeY, skewX } = shadowInfo;
        // 外阴影 | 内阴影
        let type = isOuter ? SHADOW_TYPE.OUTER : SHADOW_TYPE.INNER;
        // 透视
        if (skewX || (sizeX !== sizeY)) {
            type = SHADOW_TYPE.PERSPECTIVE;
        }
        return type;
    }

    private handleShadowPresetReport = (shadow: IShadowModelVal) => {
        let ver5;
        if (!shadow) {
            ver5 = 1;
        } else {
            ver5 = shadow?.isOuter ? 2 : 3;
        }
        this.tdwDrawerReport(FormItemName.FONT_SHADOW, { ver5 });
    };

    private handleShadowReport = (value, from?: any, onlyChangeUI?: boolean) => {
        if (typeof value === 'number') {
            this.tdwDrawerReportAngle(FormItemName.FONT_SHADOW_ANGLE, from, value, onlyChangeUI);
        } else {
            let reportId;
            let ver6;
            if (value?.size) {
                reportId  = FormItemName.FONT_SHADOW_SIZE;
                ver6 = value.size;
            }
            if (value?.blurRad) {
                reportId  = FormItemName.FONT_SHADOW_BLUR;
                ver6 = value.blurRad;
            }
            if (value?.dist) {
                reportId  = FormItemName.FONT_SHADOW_DISTANCE;
                ver6 = value.dist;
            }
            if (value?.opacity) {
                reportId  = FormItemName.FONT_SHADOW_OPACITY;
                ver6 = value.opacity;
            }
            reportId && this.tdwDrawerReportNumberInput(reportId, { ver6 }, from, onlyChangeUI);
        }
    };
}
